import React, { useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import moment from 'moment';

//* HOCs
import { withUserContext, withUIContext } from 'context'

//* Components
import { ButtonSimple, FormContainer, Input, Text, Icon, DatePicker, SelectTopics, SelectViewers } from 'components/common'
import { Form, Radio } from "antd"

const DebateForm = ({ debate, topics, viewers, createDebate, updateDebate, user, isEdit, btnLoading, loadingBtnLogic }) => {
    const router = useRouter()

    const [isPublic, setIsPublic] = useState(debate ? debate.isPublic : true)
    const [savingDraft, setSavingDraft] = useState(false)

    const formRef = useRef()
    const publishBtnRef = useRef()
    const draftBtnRef = useRef()

    const handleSubmit = (values) => {
        if (isEdit) {
            return updateDebate(debate.id, values)
        } else {
            return createDebate(values)
        }
    }

    const draftDebate = useCallback(() => {
        return formRef.current.validateFields(['title'])
            .then(() => {
                setSavingDraft(true)
                const values = formRef.current.getFieldsValue()

                return handleSubmit({ ...values, status: 'draft' }).then(() => {

                    setTimeout(() => {
                        setSavingDraft(false)
                    }, [300])

                    router.push(`/${user.username}`)
                })
            }).catch(() => {
                window.scrollTo(0, 0)
            })
    }, [])

    const onFinish = useCallback((values) => {
        return handleSubmit({ ...values, status: 'ongoing' }).then((res) => {
            router.push(`/debate/${res.slug}`)
        })
    }, [])

    const onChange = useCallback((newVal) => {
        if (newVal['isPublic'] != undefined) {
            setIsPublic(newVal.isPublic)
        }
    }, [])

    return (
        <FormContainer
            ref={formRef}
            onFinish={onFinish}
            onChange={onChange}
            dates={['endDate']}
            initialValues={{
                isPublic: true,
                viewersInvite: true,
                makePublic: true,
                ...debate,
                endDate: null
            }}
            className='flexBoxColumn debate-form'>
            <Text tag="h3" className="h3 PopSemi" text="Debate creation" />
            <Text tag="p" className="title darkGrey h7 PopReg" text="Please check the statement and summary. You will not be able to update it as soon as it is created" />
            <Input
                label={<Text tag='h5' className='h5 PopSemi' text='Debating statement' />}
                type='textarea'
                name={'title'}
                placeholder={'Add a question that you would like to discuss'}
                errorKey={'Title'}
                rules={[
                    { min: 20, message: 'Minumum characters should be 20' },
                    { max: 100, message: 'Maximum characters should be 100' }
                ]}
                inputOptions={{
                    showCount: true,
                    minLength: 20,
                    maxLength: 100,
                }}
            />
            <Input
                type='textarea'
                label={'Summary'}
                name={'content'}
                placeholder={'Add information about the discussion'}
                inputOptions={{
                    showCount: true,
                    maxLength: 1000,
                }}
                rules={[
                    { min: 150, message: 'Minumum characters should be 150' },
                ]}
            />
            <Text tag="h5" className="h5 PopSemi ant-form-item-label" text="Privacy" />
            <Form.Item name={'isPublic'}>
                <Radio.Group>
                    <Radio value={true}>
                        <Text tag='h6' className='h6 PopSemi' text='Public' />
                        <Text tag='h7' className='h7 PopReg grey' text='Everyone can see and participate' />
                    </Radio>
                    <Radio value={false}>
                        <Text tag='h6' className='h6 PopSemi' text='Private' />
                        <Text tag='h7' className='h7 PopReg grey' text='Only for invited people, or who has the link' />
                    </Radio>
                </Radio.Group>
            </Form.Item>
            {!isPublic && (
                <>
                    <SelectViewers
                        list={viewers}
                        dropdownClassName={'viewers'}
                        rules={[
                            { required: true, message: 'Viewers are required' }
                        ]}
                    />
                    <Text tag="h5" className="h5 PopSemi ant-form-item-label" text="Let other people invite users" />
                    <Form.Item name={'viewersInvite'}>
                        <Radio.Group>
                            <Radio value={true}>
                                <Text tag='h6' className='h6 PopSemi' text='Yes' />
                                <Text tag='h7' className='h7 PopSemi grey' text='Users can invite others' />
                            </Radio>
                            <Radio value={false}>
                                <Text tag='h6' className='h6 PopSemi' text='No' />
                                <Text tag='h7' className='h7 PopSemi grey' text='Users can’t invite others' />
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                </>
            )}
            <SelectTopics
                menuItemSelectedIcon={<Icon className='op-marks purple' />}
                label={
                    <>
                        <Text tag='h5' className='h5 PopSemi' text='Topics' />
                        <Text tag='h5' className='h5 PopReg grey' text='Up to 3 tags' />
                    </>
                }
                name={'topics'}
                list={topics}
                errorKey={'Topics'}
            />

            <DatePicker
                label={'End date'}
                name={'endDate'}
                inputOptions={{
                    placeholder: 'Select end date',
                    disabledDate: current => (current && current < moment() || current > moment().add(1, "month"))
                }} />

            {!isPublic && (
                <>
                    <Text tag="h5" className="h5 PopSemi ant-form-item-label" text="Make public after the deadline" />
                    <Form.Item name={'makePublic'}>
                        <Radio.Group>
                            <Radio value={true}>
                                <Text tag='h6' className='h6 PopSemi' text='Yes' />
                            </Radio>
                            <Radio value={false}>
                                <Text tag='h6' className='h6 PopSemi' text='No' />
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                </>
            )}
            <div style={{ width: '100%' }} className="buttons-block flexBox spaceBetween">
                <ButtonSimple
                    className="grey"
                    text="Clear"
                    onClick={() => formRef.current.resetFields()}
                />
                <div className='flexBox'>
                    <ButtonSimple
                        ref={draftBtnRef}
                        className='white purple'
                        text={`${draftBtnRef.current > 0 ? 'Saved' : 'Draft Debate'}`}
                        savedBtn={savingDraft}
                        disabled={savingDraft}
                        onClick={draftDebate}
                        svgFillColor={'#823ff0'}
                    />
                    <ButtonSimple
                        ref={publishBtnRef}
                        submit
                        savedBtn={btnLoading}
                        disabled={btnLoading}
                        className="align-end submit-reg-btn"
                        text={`${publishBtnRef.current > 0 ? 'Saved' : 'Publish debate'}`}
                    />
                </div>
            </div>
        </FormContainer>
    )
}

export default withUserContext(withUIContext(DebateForm, ['btnLoading', 'loadingBtnLogic']), ['createDebate', 'user', 'updateDebate'])
